import React, { useCallback, useMemo, useState } from 'react';

import _ from 'lodash';
import { Box } from '@mui/system';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { SubmitHandler } from 'react-hook-form';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { FaOptionIcon, productsIcon } from '@europrocurement/l2d-icons';
import {
    FlexyHeaderForm,
    FlexyInput,
    FormSectionSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import {
    DossiersStateType,
    FactureAchat,
    RubriqueFacturation,
    TauxTvaApiObject,
} from '@europrocurement/l2d-domain';
import { FlexyForm, defaultGridProps, inputDefaultProps } from '@europrocurement/flexy-form';
import { FactureAchatLigneJsonldFactureAchatRead } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import { useLoadingStatus } from '@europrocurement/l2d-hooks';
import useSectionDataManager from '../../../../../../hooks/useSectionDataManager';
import { FactureFormObject, LigneProduit, RSFRubFac, ToggableSection } from '../../types';
import { findRubFactById } from '../../functions/produitFunctions';
import { ModeProps } from '../../formElements/types';
import productsArrayStructure from '../../formElements/fragments/productsSection/productsArrayStructure';
import HeaderStructure from '../../../fragments/HeaderStructure';
import {
    compareApiLines,
    compareProduits,
    convertToNumberAndFormatToString,
    updateFormTotaux,
} from '../../functions/calculsProduits';

export type UpdateProductsSectionFormProps = {
    invoice: FactureAchat;
    lines: Array<FactureAchatLigneJsonldFactureAchatRead>;
    onFetchNewLines: () => void;
} & ToggableSection;

/**
 * Component for updating the products section of an invoice.
 *
 * Manages display and submission of product lines, including those with missing details.
 */
const UpdateProductsSectionForm: React.FunctionComponent<UpdateProductsSectionFormProps> =
    function ({ invoice, lines, onFetchNewLines, switchLockValues }) {
        const { getSwitchLockValues, stateSwitchLockValues } = switchLockValues;
        const [isLoading, setIsLoading] = useState<boolean>(false);

        const invoiceCategoriesDataSource: DataSource<RubriqueFacturation> = useSelector(
            (s: DossiersStateType) => s.dossiers.rubfac.main,
            _.isEqual,
        );

        const vatRatesDataSource: DataSource<TauxTvaApiObject> = useSelector(
            (s: DossiersStateType) => s.dossiers.txtva.main,
            _.isEqual,
        );

        const invoiceCategories = useMemo(
            () => [...invoiceCategoriesDataSource.data, RSFRubFac],
            [invoiceCategoriesDataSource.data],
        );

        const vatRates = useMemo<Array<number>>(
            () =>
                vatRatesDataSource.data
                    .filter(
                        (vatRate) => vatRate.tauxAchat !== null && vatRate.tauxAchat !== undefined,
                    )
                    .map((vatRate) =>
                        Number.parseFloat(convertToNumberAndFormatToString(vatRate.tauxAchat)),
                    ),
            [vatRatesDataSource.data],
        );

        const productsMode = useMemo<ModeProps>(
            () => ({ type: 'update', lock: stateSwitchLockValues.products.value }),
            [stateSwitchLockValues.products.value],
        );

        const productsStructure = useMemo(
            () =>
                productsArrayStructure({
                    rubFacts: invoiceCategories,
                    txTva: vatRates,
                    mode: productsMode,
                    handleTotalsLock: (formContextOnChangeInput) => {
                        updateFormTotaux({
                            formContext: formContextOnChangeInput,
                        });
                    },
                }),
            [invoiceCategories, productsMode, vatRates],
        );

        const formatApiLineToFormLine = useCallback(
            (line: FactureAchatLigneJsonldFactureAchatRead): Partial<LigneProduit> => {
                const productLine: Partial<LigneProduit> = {
                    idLigne: line.id,
                    rubriqueFacturation: {
                        id: 0,
                        code: undefined,
                    },
                    ht: 0,
                    txtva: {
                        label: 0,
                        value: 0,
                    },
                    tva: '',
                    ttc: '',
                };

                if (line.details && line.details.length > 0) {
                    const detail = line.details[0];
                    const foundRubFact = findRubFactById(
                        invoiceCategories,
                        detail.idRubriqueFacturation,
                    );

                    if (foundRubFact) {
                        productLine.rubriqueFacturation = {
                            id: foundRubFact.id,
                            code: foundRubFact.code,
                        };
                    }

                    productLine.ht = detail.ht ? parseFloat(detail.ht) : 0;

                    if (detail.tauxTva !== undefined) {
                        productLine.txtva = {
                            label: detail.tauxTva !== 0 ? detail.tauxTva / 100 : 0,
                            value: detail.tauxTva !== 0 ? detail.tauxTva / 100 : 0,
                        };
                    }

                    if (detail.tva) {
                        productLine.tva = convertToNumberAndFormatToString(detail.tva);
                    }

                    if (detail.ht && detail.tva) {
                        productLine.ttc = convertToNumberAndFormatToString(
                            parseFloat(detail.ht) + parseFloat(detail.tva),
                        );
                    }
                }

                return productLine;
            },
            [invoiceCategories],
        );

        const linesSort = lines.sort(compareApiLines);

        const formObject = useMemo<Partial<FactureFormObject>>(
            () => ({
                produits: linesSort
                    .map((line) => formatApiLineToFormLine(line))
                    .sort(compareProduits),
            }),
            [formatApiLineToFormLine, linesSort],
        );

        const { products: productsSection } = useSectionDataManager();
        const { updateProducts } = productsSection();

        const handleSubmit = useCallback<SubmitHandler<Pick<FactureFormObject, 'produits'>>>(
            async (values) => {
                setIsLoading(true);
                await updateProducts({
                    invoice,
                    lines,
                    newProducts: values,
                    onFetchNewLines,
                }).finally(() => {
                    stateSwitchLockValues.products.set(true);
                    setIsLoading(false);
                });
            },
            [invoice, lines, onFetchNewLines, stateSwitchLockValues.products, updateProducts],
        );

        const formatLineValues = useCallback(
            (line: FactureAchatLigneJsonldFactureAchatRead) => {
                const lineValues = {
                    rubrique: 'N/A',
                    ht: 'N/A',
                    txTva: 'N/A',
                    tva: 'N/A',
                    ttc: 'N/A',
                };

                if (line.details && line.details.length > 0) {
                    const detail = line.details[0];

                    if (detail.idRubriqueFacturation) {
                        const foundRubFact = findRubFactById(
                            invoiceCategories,
                            detail.idRubriqueFacturation,
                        );
                        if (foundRubFact) {
                            lineValues.rubrique = foundRubFact.code;
                        }
                    }

                    lineValues.ht = detail.ht ? convertToNumberAndFormatToString(detail.ht) : 'N/A';
                    lineValues.txTva =
                        detail.tauxTva !== undefined ? (detail.tauxTva / 100).toString() : '0';
                    lineValues.tva = detail.tva
                        ? convertToNumberAndFormatToString(detail.tva)
                        : 'N/A';

                    if (detail.ht && detail.tva) {
                        lineValues.ttc = convertToNumberAndFormatToString(
                            parseFloat(detail.ht) + parseFloat(detail.tva),
                        );
                    }
                }

                return lineValues;
            },
            [invoiceCategories],
        );

        const readyToDisplay = useMemo<boolean>(
            () => !!(!isLoading && invoice && lines && lines.length > 0),
            [invoice, isLoading, lines],
        );

        const { loading } = useLoadingStatus({
            checkReady: () => readyToDisplay,
        });

        return (
            <SkeletonLoader
                isLoading={loading}
                skeleton={<FormSectionSkeleton fragmentOnlyRender={false} />}
            >
                <>
                    <FlexyHeaderForm
                        label={
                            <HeaderStructure
                                icon={
                                    <FaOptionIcon
                                        {...productsIcon.props}
                                        size="sm"
                                    />
                                }
                                title={productsIcon.displayName}
                            />
                        }
                        switches={[
                            ...(!invoice?.syncCegid
                                ? [getSwitchLockValues({ sectionToBlock: 'products' })]
                                : []),
                        ]}
                        outlined
                    />
                    {stateSwitchLockValues.products.value ? (
                        <Box sx={{ marginTop: '10px' }}>
                            {linesSort.map((line) => {
                                const formattedLine = formatLineValues(line);
                                const lineHasDetails = line.details && line.details.length > 0;

                                return (
                                    <Grid
                                        key={`line-${line.id}-${lineHasDetails ? line.details[0].id : 'no-details'}`}
                                        container
                                        {...defaultGridProps}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            md={12 / 5}
                                        >
                                            <FlexyInput
                                                {...inputDefaultProps}
                                                variant="outlined"
                                                name="rubriqueFacturation"
                                                type="displayData"
                                                inputlabel="Rubrique"
                                                value={
                                                    lineHasDetails
                                                        ? formattedLine.rubrique
                                                        : 'Pas de détails'
                                                }
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={12 / 5}
                                        >
                                            <FlexyInput
                                                {...inputDefaultProps}
                                                variant="outlined"
                                                name="ht"
                                                type="displayData"
                                                inputlabel="HT"
                                                value={
                                                    lineHasDetails ? `${formattedLine.ht} €` : 'N/A'
                                                }
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={12 / 5}
                                        >
                                            <FlexyInput
                                                {...inputDefaultProps}
                                                variant="outlined"
                                                name="txtva"
                                                type="displayData"
                                                inputlabel="Taux TVA"
                                                value={
                                                    lineHasDetails
                                                        ? `${formattedLine.txTva} %`
                                                        : 'N/A'
                                                }
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={12 / 5}
                                        >
                                            <FlexyInput
                                                {...inputDefaultProps}
                                                variant="outlined"
                                                name="tva"
                                                type="displayData"
                                                inputlabel="TVA"
                                                value={
                                                    lineHasDetails
                                                        ? `${formattedLine.tva} €`
                                                        : 'N/A'
                                                }
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={12 / 5}
                                        >
                                            <FlexyInput
                                                {...inputDefaultProps}
                                                variant="outlined"
                                                name="ttc"
                                                type="displayData"
                                                inputlabel="TTC"
                                                value={
                                                    lineHasDetails
                                                        ? `${formattedLine.ttc} €`
                                                        : 'N/A'
                                                }
                                                readOnly
                                            />
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </Box>
                    ) : (
                        <FlexyForm
                            onSubmit={handleSubmit}
                            formObject={formObject}
                            formStructure={productsStructure}
                        />
                    )}
                </>
            </SkeletonLoader>
        );
    };

export default UpdateProductsSectionForm;
